'use client';

/**
 * TypeformWidget — client leaf for Typeform `data-tf-widget` elements.
 *
 * Source HTML looks like:
 *   <div data-tf-widget="<id>" data-tf-opacity="100" data-tf-iframe-props="title=..."
 *        data-tf-transitive-search-params data-tf-disable-scroll data-tf-auto-resize
 *        data-tf-medium="snippet" data-tf-hidden="utm_source=xxxxx,..."
 *        style="width:100%;height:500px;"></div>
 *   <script src="//embed.typeform.com/next/embed.js"></script>
 *
 * `embed.js` upgrades any `data-tf-widget` div into an iframe at load time.
 * We render the div with the source-declared attributes and load `embed.js`
 * exactly once per page via a module-level singleton promise.
 *
 * Source `data-tf-hidden` placeholder values like `utm_source=xxxxx,...` are
 * preserved verbatim — Typeform replaces them with real URL params at runtime.
 */

import { useEffect, useRef } from 'react';

let embedJsPromise: Promise<void> | null = null;

function loadTypeformEmbedJs(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (embedJsPromise) return embedJsPromise;

  embedJsPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="embed.typeform.com/next/embed.js"]',
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('embed.js failed')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://embed.typeform.com/next/embed.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      embedJsPromise = null;
      reject(new Error('embed.js failed'));
    };
    document.head.appendChild(script);
  });

  return embedJsPromise;
}

interface TypeformWidgetProps {
  widgetId: string;
  title: string;
  heightPx?: number;
  widthCss?: string;
  opacityPercent?: number;
  transitiveSearchParams?: boolean;
  disableScroll?: boolean;
  autoResize?: boolean;
  medium?: string;
  hidden?: string;
  className?: string;
}

export function TypeformWidget({
  widgetId,
  title,
  heightPx,
  widthCss,
  opacityPercent,
  transitiveSearchParams,
  disableScroll,
  autoResize,
  medium,
  hidden,
  className,
}: TypeformWidgetProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTypeformEmbedJs().catch(() => {
      // Failure non-fatal — unupgraded div remains.
    });
  }, []);

  const style: React.CSSProperties = {};
  if (widthCss) style.width = widthCss;
  if (heightPx) style.height = `${heightPx}px`;

  const dataAttrs: Record<string, string> = {
    'data-tf-widget': widgetId,
  };
  if (opacityPercent !== undefined) dataAttrs['data-tf-opacity'] = String(opacityPercent);
  if (transitiveSearchParams) dataAttrs['data-tf-transitive-search-params'] = '';
  if (disableScroll) dataAttrs['data-tf-disable-scroll'] = '';
  if (autoResize) dataAttrs['data-tf-auto-resize'] = '';
  if (medium) dataAttrs['data-tf-medium'] = medium;
  if (hidden) dataAttrs['data-tf-hidden'] = hidden;
  dataAttrs['data-tf-iframe-props'] = `title=${title}`;

  return <div ref={ref} className={className} style={style} {...dataAttrs} />;
}
